import { Suspense } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { graphql } from "relay-runtime"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { joinStrings } from "@/lib/joinString"
import { getPersistedLanguage } from "@/lib/language-storage"
import { countrySelectorSchema } from "@/models/patient"
import { addressDialogCountriesQuery } from "@/modules/care-provider/advanced-search/__generated__/addressDialogCountriesQuery.graphql"
import { AddressOption } from "@/modules/care-provider/advanced-search/care-provider-address-filter/care-provider-address-select"
import { CitySelector } from "@/modules/patients/components/city-selector"
import { CountrySelector } from "@/modules/patients/components/country-selector"
import { HouseNumberStreet } from "@/modules/patients/components/house-number-street"
import { PostalCodeSelector } from "@/modules/patients/components/postalcode-selector"
import { useCareProviderManualAddressStoreContext } from "@/stores/useCareProviderManualAddressStore"

interface Props {
  country?: string
  onClose: VoidFunction
  onAddressSearch: (address: AddressOption) => void
}

const addressSchema = countrySelectorSchema.extend({ houseNumberStreet: z.string().optional().default("") })
type AddressFormSchema = z.infer<typeof addressSchema>
type AddressFormInput = z.input<typeof addressSchema>

const AddressDialog = ({ onClose, onAddressSearch, country = "CH" }: Props) => {
  const { t } = useTranslation()
  const setAddresses = useCareProviderManualAddressStoreContext((state) => state.setAddresses)
  const language = getPersistedLanguage()

  const { displayableCountries: countries } = useLazyLoadQuery<addressDialogCountriesQuery>(
    graphql`
      query addressDialogCountriesQuery($language: String!) {
        displayableCountries(language: $language) {
          id
          language
          countryName
          countryIsoCode2
        }
      }
    `,
    { language },
  )

  const form = useForm<AddressFormInput, unknown, AddressFormSchema>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      country,
      houseNumberStreet: "",
    },
  })

  const onCancel = () => {
    form.reset(undefined, { keepDefaultValues: true })
    onClose()
  }

  const onSubmit = (data: AddressFormSchema) => {
    const { country, city, houseNumberStreet, postalCode } = data

    const countryName = countries.find(
      (c) => c.countryIsoCode2 === country && language.toUpperCase() === c.language,
    )?.countryName

    const postalCodeCity = joinStrings([postalCode, city], " ")
    const patientAddress = joinStrings([houseNumberStreet, postalCodeCity, countryName], ", ")

    const manualAddress: AddressOption = {
      countryCode: country,
      municipality: city,
      streetName: houseNumberStreet,
      postalCode,
      label: patientAddress,
    }

    setAddresses(manualAddress)
    onAddressSearch(manualAddress)
    onCancel()
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        variant="small"
        data-testid="choose-address-content"
        onPointerDownOutside={(e) => e.preventDefault()}>
        <form id="address-form" data-testid="address-form" onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader data-testid="choose-address-header">
            <DialogTitle data-testid="choose-address-title">{t("patient.addressDialog.chooseAddress")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 px-6 py-2" data-testid="address-body">
            <Suspense fallback={null}>
              <FormProvider {...form}>
                <div className="grid w-full grid-cols-4 gap-4">
                  <CountrySelector />
                  <PostalCodeSelector />
                  <CitySelector required />
                  <HouseNumberStreet required={false} />
                </div>
              </FormProvider>
            </Suspense>
          </div>
          <DialogFooter data-testid="address-footer">
            <Button variant="secondary" onClick={onCancel} type="button" data-testid="cancel-button">
              {t("common.button.cancel")}
            </Button>
            <Button data-testid="submit-button" type="submit">
              {t("common.button.searchThisAddress")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContentCustom>
    </Dialog>
  )
}

export default AddressDialog
